import { useMemo, useRef, useState } from "react";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import AnalyticsWatchAreasModal from "@/components/AnalyticsWatchAreasModal";
import { deriveRecommendationConfidence } from "@/utils/analyticsConfidence";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import "../pages/AnalyticsExecutiveInsightsTab.css";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");

const fmtL = (v) => {
  if (typeof v !== "number") return v ?? "—";
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
  return String(v);
};

const fmtK = (v) => {
  if (typeof v !== "number") return v ?? "—";
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const stripMd = (text) =>
  String(text || "")
    .replace(/\*\*/g, "")
    .replace(/^[-*]\s+/gm, "")
    .trim();

const parseBullets = parseInsightBullets;

const computeReadiness = (m) => {
  const classroom = Number(m?.classroom_health ?? 0);
  const toilet = Number(m?.toilet_functional ?? 0);
  const apaar = Number(m?.apaar_rate ?? 0);
  const dropoutScore = Math.max(0, 100 - Math.min(100, Number(m?.dropout_rate ?? 0) * 20));
  return Math.round((classroom + toilet + apaar + dropoutScore) / 4);
};

const buildWatchAreas = (m) => {
  const areas = [];
  const apaar = Number(m?.apaar_rate ?? 0);
  if (apaar < 90) {
    areas.push({
      id: "apaar",
      label: "APAAR Generation",
      value: `${apaar}%`,
      target: "≥ 90%",
      gap: `${(90 - apaar).toFixed(1)}% below target`,
      severity: apaar < 80 ? "critical" : "watch",
      action: "Launch school-wise APAAR closure drive and assign daily exception lists to block officers.",
    });
  }

  const dropout = Number(m?.dropout_rate ?? 0);
  if (dropout > 3) {
    areas.push({
      id: "dropout",
      label: "Dropout Risk",
      value: `${dropout}%`,
      target: "≤ 3%",
      gap: `${(dropout - 3).toFixed(1)}% above threshold`,
      severity: dropout > 5 ? "critical" : "watch",
      action: "Validate attendance, transfer records and school remarks in high-rate blocks before escalation.",
    });
  }

  const classroom = Number(m?.classroom_health ?? 0);
  if (classroom < 95) {
    areas.push({
      id: "classroom",
      label: "Classroom Health",
      value: `${classroom}%`,
      target: "≥ 95%",
      gap: `${(95 - classroom).toFixed(1)}% below target`,
      severity: classroom < 90 ? "critical" : "watch",
      action: "Prioritize blocks below district average for infrastructure remediation planning.",
    });
  }

  const toilet = Number(m?.toilet_functional ?? 0);
  if (toilet < 95) {
    areas.push({
      id: "toilet",
      label: "Toilet Functionality",
      value: `${toilet}%`,
      target: "≥ 95%",
      gap: `${(95 - toilet).toFixed(1)}% below target`,
      severity: toilet < 90 ? "critical" : "watch",
      action: "Schedule preventive sanitation review for schools with non-functional toilet blocks.",
    });
  }

  return areas;
};

const countWatchAreas = (m) => buildWatchAreas(m).length;

const formatGenerated = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date} · ${time}`;
};

const defaultSignals = (m) => [
  {
    iconClass: "orange",
    title: "Incomplete student identity mapping",
    text: `APAAR rate is lower than core infrastructure indicators (${m?.apaar_rate ?? "—"}%), suggesting process and data-collection bottlenecks.`,
    impact: "high",
  },
  {
    iconClass: "red",
    title: "Dropout outlier concentration",
    text: `Dropout risk is not uniform at ${m?.dropout_rate ?? "—"}%. Some schools require validation of attendance, transfer and retention data.`,
    impact: "high",
  },
  {
    iconClass: "green",
    title: "Sanitation performance is reliable",
    text: `Toilet functionality is ${m?.toilet_functional ?? "—"}% and should be maintained through periodic rather than urgent review.`,
    impact: Number(m?.toilet_functional ?? 0) >= 95 ? "low" : "medium",
  },
  {
    iconClass: "purple",
    title: "Teacher readiness may affect outcomes",
    text: `${fmt(m?.teachers)} teachers tracked. Teacher data should be linked with school-level learning and attendance indicators for deeper diagnosis.`,
    impact: "medium",
  },
];

const defaultRecommendations = (m) => [
  {
    title: "Launch APAAR closure drive",
    confidence: deriveRecommendationConfidence(m, 0),
    text: `Prioritize schools below 80% APAAR generation (district average ${m?.apaar_rate ?? "—"}%) and assign daily exception lists to block officers.`,
  },
  {
    title: "Validate dropout risk schools",
    confidence: deriveRecommendationConfidence(m, 1),
    text: "Triangulate dropout risk with attendance logs, transfer records, school visits and teacher remarks.",
  },
  {
    title: "Protect infrastructure gains",
    confidence: deriveRecommendationConfidence(m, 2),
    text: `Keep classroom (${m?.classroom_health ?? "—"}%) and toilet indicators on monthly preventive monitoring instead of emergency escalation.`,
  },
  {
    title: "Link teacher quality with outcomes",
    confidence: deriveRecommendationConfidence(m, 3),
    text: "Create a combined view of CTE status, staffing gaps, learning outcomes and attendance.",
  },
];

const deriveActions = (m) => {
  const apaar = Number(m?.apaar_rate ?? 0);
  const dropout = Number(m?.dropout_rate ?? 0);
  const classroom = Number(m?.classroom_health ?? 0);
  const items = [];

  if (apaar < 90) {
    items.push({
      title: "Create 7-day APAAR exception list",
      text: `District APAAR is ${apaar}% — generate school-wise pending lists and assign daily closure targets to block officers.`,
    });
  }
  if (dropout > 2) {
    items.push({
      title: "Validate top dropout risk schools",
      text: `Dropout signal is ${dropout}% — review attendance, transfers and school remarks before escalating as true dropout cases.`,
    });
  }
  if (classroom < 95) {
    items.push({
      title: "Schedule infrastructure remediation review",
      text: `Classroom health is ${classroom}% — prioritize blocks below district average for repair planning.`,
    });
  }
  if (items.length < 3) {
    items.push({
      title: "Hold fortnightly district review",
      text: "Track movement on APAAR, dropout risk and teacher readiness until watch-area indicators improve.",
    });
  }
  return items.slice(0, 3).map((item, i) => ({
    ...item,
    num: String(i + 1).padStart(2, "0"),
  }));
};

const buildBriefingText = (m, insightsText) => {
  const first = parseBullets(insightsText)[0];
  if (first && first.length > 40) return first;

  const infraStrong = Number(m?.classroom_health ?? 0) >= 90;
  const apaarGap = Number(m?.apaar_rate ?? 0) < 90;
  const dropoutWatch = Number(m?.dropout_rate ?? 0) > 3;

  if (infraStrong && (apaarGap || dropoutWatch)) {
    return "The district is broadly stable. Physical infrastructure is strong, but APAAR completion and dropout risk require targeted block-level follow-up before the next monthly review.";
  }
  if (!infraStrong) {
    return "Infrastructure and completion indicators need coordinated follow-up. Leadership should prioritize the weakest KPIs with block-level accountability before the next review.";
  }
  return "The district shows a stable baseline across core indicators. Continue preventive monitoring while closing remaining APAAR and data-quality gaps.";
};

const buildDecision = (m) => {
  const apaar = Number(m?.apaar_rate ?? 0);
  const dropout = Number(m?.dropout_rate ?? 0);
  const classroom = Number(m?.classroom_health ?? 0);
  const focus = [];
  if (apaar < 90) focus.push("APAAR completion");
  if (dropout > 3) focus.push("dropout watchlist validation");
  if (classroom < 90) focus.push("infrastructure remediation");
  if (!focus.length) focus.push("preventive monitoring of core KPIs");
  return `Approve a 30-day action sprint focused on ${focus.join(", ")}.`;
};

const AnalyticsExecutiveInsightsTab = ({
  executiveData,
  loading = false,
  onRefresh,
  onGenerate,
  scopeLabel = "Pune District",
}) => {
  const signalsRef = useRef(null);
  const actionsRef = useRef(null);
  const topRef = useRef(null);
  const [watchAreasOpen, setWatchAreasOpen] = useState(false);

  const metrics = executiveData?.metrics || {};
  const readiness = useMemo(() => computeReadiness(metrics), [metrics]);
  const watchAreaRows = useMemo(() => buildWatchAreas(metrics), [metrics]);
  const watchAreas = watchAreaRows.length;

  const gaugeStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--aei-primary) 0 ${readiness}%, rgba(37,99,235,.12) ${readiness}% 100%)`,
    }),
    [readiness],
  );

  const buckets = useMemo(
    () => splitIntoInsightBuckets(executiveData?.ai_summary || ""),
    [executiveData?.ai_summary],
  );

  const briefingText = useMemo(
    () => buildBriefingText(metrics, buckets.insights),
    [metrics, buckets.insights],
  );

  const defaultRoot = useMemo(
    () => defaultSignals(metrics).map((s) => `${s.title}: ${s.text}`),
    [metrics],
  );

  const defaultRecs = useMemo(
    () => defaultRecommendations(metrics).map((r) => `${r.title}: ${r.text}`),
    [metrics],
  );

  const defaultActions = useMemo(
    () => deriveActions(metrics).map((a) => `${a.title}: ${a.text}`),
    [metrics],
  );

  const priorityRows = useMemo(() => {
    const apaar = Number(metrics.apaar_rate ?? 0);
    const dropout = Number(metrics.dropout_rate ?? 0);
    const classroom = Number(metrics.classroom_health ?? 0);
    const toilet = Number(metrics.toilet_functional ?? 0);
    const teachers = Number(metrics.teachers ?? 0);
    const teacherQuality = Number(metrics.teacher_quality ?? 0);

    const dropoutProgress = Math.min(100, Math.round(((5 - dropout) / 2) * 100));

    return [
      {
        priority: 1,
        area: "APAAR generation",
        current: `${apaar}%`,
        target: "95%",
        progress: apaar,
        progressClass: "",
        status: "High",
        statusClass: "red",
        owner: "Block Education Officer",
      },
      {
        priority: 2,
        area: "Dropout verification",
        current: `${dropout}%`,
        target: "<3%",
        progress: dropoutProgress,
        progressClass: "orange",
        status: "High",
        statusClass: "red",
        owner: "Cluster Resource Team",
      },
      {
        priority: 3,
        area: "Classroom health",
        current: `${classroom}%`,
        target: "97%",
        progress: classroom,
        progressClass: "green",
        status: "Stable",
        statusClass: "green",
        owner: "Infrastructure Cell",
      },
      {
        priority: 4,
        area: "Toilet functionality",
        current: `${toilet}%`,
        target: "98%",
        progress: toilet,
        progressClass: "green",
        status: toilet >= 98 ? "On Track" : "Watch",
        statusClass: toilet >= 98 ? "green" : "",
        owner: "School Admin",
      },
      {
        priority: 5,
        area: "Teacher readiness mapping",
        current: teacherQuality > 0 ? `${teacherQuality}% CTET` : fmtK(teachers),
        target: "100% CTET",
        progress: teacherQuality > 0 ? teacherQuality : 0,
        progressClass: teacherQuality >= 80 ? "green" : "",
        status: teacherQuality >= 80 ? "On Track" : "Watch",
        statusClass: teacherQuality >= 80 ? "green" : "",
        owner: "Teacher Dashboard Team",
      },
    ];
  }, [metrics]);

  const scrollTo = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading && !executiveData) {
    return (
      <div className="analytics-executive-view" data-testid="analytics-executive-tab">
        <div className="aei-loading">
          <div className="aei-loading-spinner" />
          <span>Generating executive briefing…</span>
        </div>
      </div>
    );
  }

  if (!executiveData) {
    return (
      <div className="analytics-executive-view" data-testid="analytics-executive-tab">
        <div className="aei-loading">
          <p className="dashboard-kpi-hero-title" style={{ margin: 0, color: "var(--aei-text)" }}>
            AI Executive Briefing
          </p>
          <p style={{ margin: 0, maxWidth: 420, textAlign: "center" }}>
            Comprehensive AI-generated insights for leadership review across the selected district scope.
          </p>
          <button type="button" className="aei-btn aei-btn-primary" onClick={onGenerate}>
            Generate Briefing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-executive-view" data-testid="analytics-executive-tab" ref={topRef}>
      <section className="aei-page-header">
        <div>
          <div className="aei-eyebrow">
            <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20" />
              <path d="m17 5-5-3-5 3" />
              <path d="m17 19-5 3-5-3" />
              <path d="M2 12h20" />
              <path d="m5 7-3 5 3 5" />
              <path d="m19 7 3 5-3 5" />
            </svg>
            AI-powered predictions &amp; insights · {scopeLabel}
          </div>
          <h1>Advanced Analytics</h1>
          <p>
            Executive-ready summary of school health, risk signals, root causes and action priorities across the
            selected district scope.
          </p>
        </div>
        <div className="aei-header-actions">
          <button type="button" className="aei-btn" title="Export">
            <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            Export
          </button>
          <button type="button" className="aei-btn aei-btn-primary" onClick={onRefresh} disabled={loading}>
            <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <section className="aei-hero-card">
        <div className="aei-brief-layout">
          <div>
            <div className="aei-brief-main">
              <div className="aei-gauge" style={gaugeStyle} aria-label={`Executive readiness score ${readiness} percent`}>
                <div>
                  <strong>{readiness}%</strong>
                  <span>Readiness</span>
                </div>
              </div>
              <div className="aei-brief-copy">
                <h2 className="dashboard-kpi-hero-title">AI Executive Briefing</h2>
                <p>{briefingText}</p>
                <div className="aei-status-row">
                  <HeroStatusChip chipClass="aei-status-chip" variant="green">
                    Stable district baseline
                  </HeroStatusChip>
                  {watchAreas > 0 ? (
                    <HeroStatusChip
                      chipClass="aei-status-chip"
                      variant="amber"
                      onClick={() => setWatchAreasOpen(true)}
                      ariaLabel={`${watchAreas} watch ${watchAreas === 1 ? "area" : "areas"} — view indicators below target`}
                    >
                      {watchAreas} watch {watchAreas === 1 ? "area" : "areas"}
                    </HeroStatusChip>
                  ) : null}
                  <HeroStatusChip chipClass="aei-status-chip" showChevron={false}>
                    Generated {formatGenerated(executiveData.generated_at)}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="aei-summary-note">
              <div className="aei-icon-box purple">
                <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18h6" />
                  <path d="M10 22h4" />
                  <path d="M12 2a7 7 0 0 0-4 12.74V17h8v-2.26A7 7 0 0 0 12 2Z" />
                </svg>
              </div>
              <div>
                <b>Primary executive decision</b>
                <span>{buildDecision(metrics)}</span>
              </div>
            </div>
          </div>

          <div className="aei-metric-grid" aria-label="Executive insight KPIs">
            <article className="aei-metric-card">
              <small>Schools Covered</small>
              <strong>{fmt(metrics.schools)}</strong>
              <span>Selected school scope</span>
            </article>
            <article className="aei-metric-card purple">
              <small>Students</small>
              <strong>{fmtL(metrics.students)}</strong>
              <span>Enrolment base</span>
            </article>
            <article className="aei-metric-card green">
              <small>Classroom Health</small>
              <strong>{metrics.classroom_health ?? "—"}%</strong>
              <span>Strong infrastructure signal</span>
            </article>
            <article className="aei-metric-card orange">
              <small>APAAR Rate</small>
              <strong>{metrics.apaar_rate ?? "—"}%</strong>
              <span className={Number(metrics.apaar_rate ?? 0) < 90 ? "warn" : ""}>
                {Number(metrics.apaar_rate ?? 0) < 90 ? "Needs targeted closure" : "On track"}
              </span>
            </article>
          </div>
        </div>
      </section>

      <div className="aei-section-title">
        <div>
          <h2>Decision Snapshot</h2>
          <p>
            Four cards summarize what leadership needs to know first: what is working, what is exposed, and where action
            is required.
          </p>
        </div>
        <button type="button" className="aei-section-action" onClick={() => scrollTo(signalsRef)}>
          View root causes →
        </button>
      </div>

      <section className="aei-insight-grid">
        <article className="aei-insight-card">
          <div className="aei-insight-head">
            <div className="aei-icon-box green">
              <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
            </div>
            <span className="aei-priority-dot" />
          </div>
          <h3>Infrastructure Base Is Strong</h3>
          <p>Classroom health and toilet functionality are above the immediate intervention threshold.</p>
          <div className="aei-insight-value">
            <strong>{metrics.classroom_health ?? "—"}%</strong>
            <span>classroom health</span>
          </div>
        </article>
        <article className="aei-insight-card">
          <div className="aei-insight-head">
            <div className="aei-icon-box orange">
              <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <span className="aei-priority-dot amber" />
          </div>
          <h3>Identity Completion Gap</h3>
          <p>APAAR generation needs focused closure to avoid downstream reporting and entitlement issues.</p>
          <div className="aei-insight-value">
            <strong>{metrics.apaar_rate ?? "—"}%</strong>
            <span>APAAR rate</span>
          </div>
        </article>
        <article className="aei-insight-card">
          <div className="aei-insight-head">
            <div className="aei-icon-box red">
              <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </div>
            <span className="aei-priority-dot red" />
          </div>
          <h3>Dropout Risk Watch</h3>
          <p>Dropout signal is manageable, but school-level outliers should be verified and acted on early.</p>
          <div className="aei-insight-value">
            <strong>{metrics.dropout_rate ?? "—"}%</strong>
            <span>dropout rate</span>
          </div>
        </article>
        <article className="aei-insight-card">
          <div className="aei-insight-head">
            <div className="aei-icon-box">
              <svg className="aei-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
              </svg>
            </div>
            <span className="aei-priority-dot amber" />
          </div>
          <h3>Teacher Data Dependency</h3>
          <p>Teacher deployment quality and CTE completion should be cross-checked before final policy action.</p>
          <div className="aei-insight-value">
            <strong>{fmt(metrics.teachers)}</strong>
            <span>teachers tracked</span>
          </div>
        </article>
      </section>

      <div className="aei-section-title" ref={signalsRef}>
        <div>
          <h2>Intervention Action Plan</h2>
          <p>
            Root cause signals, recommended interventions and priority actions from the executive AI briefing.
          </p>
        </div>
        <button type="button" className="aei-section-action" onClick={() => scrollTo(actionsRef)}>
          Go to monitoring →
        </button>
      </div>

      <section className="aei-action-plan-section">
        <InterventionActionPlan
          aiContent={executiveData?.ai_summary}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="aei-section-title">
        <div>
          <h2>Priority Monitoring Table</h2>
          <p>Leadership view of focus areas, target indicators and the next review cadence.</p>
        </div>
        <button type="button" className="aei-section-action" onClick={() => scrollTo(topRef)}>
          Back to top ↑
        </button>
      </div>

      <section className="aei-panel aei-table-card aei-priority-table" ref={actionsRef}>
        <div className="aei-table-head">
          <div>
            <h3>Executive Priority Areas</h3>
            <p>Focus list for district review and block-level follow-up.</p>
          </div>
          <span className="aei-pill">Priority view</span>
        </div>
        <table aria-label="Executive priority areas">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Area</th>
              <th>Current</th>
              <th>Target</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {priorityRows.map((row) => (
              <tr key={row.area}>
                <td>{row.priority}</td>
                <td>{row.area}</td>
                <td className="aei-score">{row.current}</td>
                <td>{row.target}</td>
                <td>
                  <div className="aei-mini-progress">
                    <i className={row.progressClass || undefined} style={{ width: `${row.progress}%` }} />
                  </div>
                </td>
                <td>
                  <span className={`aei-tag ${row.statusClass || ""}`}>{row.status}</span>
                </td>
                <td>{row.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <AnalyticsWatchAreasModal
        open={watchAreasOpen}
        onClose={() => setWatchAreasOpen(false)}
        rows={watchAreaRows}
        scopeLabel={scopeLabel}
      />
    </div>
  );
};

export default AnalyticsExecutiveInsightsTab;
